import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, MessageCircle, ShoppingBag, ArrowLeft } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { formatPrice, buildWhatsAppMessage } from '../data/assets';
import { getSettings, createOrder } from '../lib/api';
import { useToast } from '../hooks/use-toast';

const Cart = () => {
  const { items, updateQty, removeFromCart, clearCart, total } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ customer_name: '', phone: '', address: '', note: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { getSettings().then(setSettings).catch(() => {}); }, []);

  const checkout = async () => {
    if (!form.customer_name || !form.phone) {
      toast({ title: 'Missing details', description: 'Please enter your name and phone number.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    let order = null;
    try {
      order = await createOrder({ ...form, items });
    } catch (e) { /* proceed anyway */ }
    const msg = buildWhatsAppMessage(items, total, { ...form, order_number: order?.order_number });
    const num = (settings.whatsapp_number || '923001234567').replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank');
    setSubmitting(false);
    clearCart();
    toast({ title: 'Order placed!', description: 'Continue on WhatsApp to confirm.' });
    navigate('/');
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <ShoppingBag size={64} className="mx-auto text-gray-300" />
        <h2 className="font-head text-2xl font-bold mt-6 text-[#14181b]">Your cart is empty</h2>
        <p className="text-gray-500 mt-2">Browse our premium LED lighting collection.</p>
        <Link to="/shop" className="nl-btn bg-[#0f7a37] hover:bg-[#0d5228] text-white px-7 py-3 mt-6 inline-flex"><ArrowLeft size={18} /> Continue Shopping</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="font-head text-3xl font-extrabold text-[#14181b] mb-8">Shopping Cart</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map(i => (
            <div key={i.product_id} className="flex gap-4 bg-white rounded-2xl border border-gray-100 p-4">
              <div className="w-24 h-24 bg-[#f6faf7] rounded-xl p-2 shrink-0">
                <img src={i.image} alt={i.name} className="w-full h-full object-contain" />
              </div>
              <div className="flex-1">
                <Link to={`/product/${i.product_id}`} className="font-head font-semibold text-[#14181b] hover:text-[#0f7a37] line-clamp-2">{i.name}</Link>
                <div className="text-[#0d5228] font-bold mt-1">{formatPrice(i.price)}</div>
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center border border-gray-200 rounded-full">
                    <button onClick={() => updateQty(i.product_id, i.quantity - 1)} className="px-2.5 py-1.5 hover:bg-gray-100 rounded-l-full"><Minus size={14} /></button>
                    <span className="px-3 text-sm font-semibold">{i.quantity}</span>
                    <button onClick={() => updateQty(i.product_id, i.quantity + 1)} className="px-2.5 py-1.5 hover:bg-gray-100 rounded-r-full"><Plus size={14} /></button>
                  </div>
                  <button onClick={() => removeFromCart(i.product_id)} className="text-red-400 hover:text-red-600 p-1.5"><Trash2 size={18} /></button>
                </div>
              </div>
              <div className="font-bold text-[#14181b]">{formatPrice(i.price * i.quantity)}</div>
            </div>
          ))}
        </div>

        {/* checkout */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-100 p-6 sticky top-40">
            <h3 className="font-head font-bold text-lg mb-4">Order Summary</h3>
            <div className="space-y-2.5 mb-4">
              <input value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} placeholder="Full Name *" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37]" />
              <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="Phone Number *" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37]" />
              <input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Delivery Address" className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37]" />
              <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Order note (optional)" rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#0f7a37]" />
            </div>
            <div className="flex justify-between py-3 border-t border-gray-100 text-lg font-extrabold text-[#0d5228]">
              <span>Total</span><span>{formatPrice(total)}</span>
            </div>
            <button onClick={checkout} disabled={submitting}
              className="nl-btn w-full bg-[#25D366] hover:bg-[#1eb457] text-white px-5 py-3.5 mt-2 disabled:opacity-60">
              <MessageCircle size={20} /> {submitting ? 'Placing...' : 'Order on WhatsApp'}
            </button>
            <Link to="/shop" className="block text-center text-sm text-[#0f7a37] mt-4 font-medium hover:underline">Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
